import React, { useState, Fragment, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  RotateCcw,
  Edit2,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Building2,
  FolderGit2,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ExternalLink,
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
  useProjects,
  useCreateProject,
  useDeleteProject,
  type NewClient,
  type ClientRow,
  type ProjectRow,
  type ProjectStatus,
} from '../lib/queries';
import Shell from '../components/Shell';
import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  initials,
  useToast,
  Select,
  StatusChip,
  formatDate,
} from '../components/ui';
import { ProjectDrawer } from './Projects';

export default function Clients() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ClientRow | null>(null);
  const [expandedClientIds, setExpandedClientIds] = useState<Set<string>>(new Set());
  const [addingProjectClientId, setAddingProjectClientId] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectRow | null>(null);

  const confirm = useConfirm<ClientRow>();
  const confirmProject = useConfirm<ProjectRow>();
  const { data, isLoading, error, refetch } = useClients(search);
  const { data: allProjects } = useProjects('', '');
  const restore = useRestore();
  const remove = useDeleteClient();
  const removeProject = useDeleteProject();
  const toast = useToast();

  function toggleExpand(clientId: string) {
    setExpandedClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  }

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'clients', id: removed.id }),
      });
      confirm.close();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the client', true);
      confirm.close();
    }
  }

  async function doDeleteProject() {
    if (!confirmProject.target) return;
    try {
      await removeProject.mutateAsync(confirmProject.target.id);
      const removed = confirmProject.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'projects', id: removed.id }),
      });
      confirmProject.close();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the project', true);
      confirmProject.close();
    }
  }

  // Summary Metrics
  const totalClients = data?.length ?? 0;
  const totalProjects = data?.reduce((sum, c) => sum + (c.projectCount ?? 0), 0) ?? 0;
  const uniqueCities = new Set(data?.map((c) => c.city).filter(Boolean)).size;

  return (
    <Shell
      title="Clients"
      crumb="Directory"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Client</span>
        </button>
      }
    >
      {/* Quick Summary KPIs */}
      {!isLoading && data && (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          style={{ marginBottom: 18 }}
        >
          <div className="kpi">
            <div className="lbl">
              <span>Total Client Accounts</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={14} />
              </div>
            </div>
            <div className="val">{totalClients}</div>
            <div className="sub">registered client profiles</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Active Projects Linked</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Briefcase size={14} />
              </div>
            </div>
            <div className="val" style={{ color: '#059669' }}>{totalProjects}</div>
            <div className="sub">cumulative client sites</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Operational Regions</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MapPin size={14} />
              </div>
            </div>
            <div className="val">{uniqueCities}</div>
            <div className="sub">cities across portfolio</div>
          </div>
        </motion.div>
      )}

      {/* Modern Filter Toolbar */}
      <div
        className="toolbar"
        style={{
          background: 'var(--surface)',
          padding: '10px 14px',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-xs)',
          marginBottom: 16,
        }}
      >
        <div className="field-inline" style={{ minWidth: 260, flex: 1 }}>
          <Search size={14} style={{ color: 'var(--text-faint)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients by name, company, phone, city..."
            aria-label="Search clients"
          />
        </div>
        {search && (
          <button
            className="btn btn-auto btn-sm btn-ghost"
            onClick={() => setSearch('')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={13} />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Clients Table Card */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.length ? (
            <EmptyState
              icon="users"
              title={search ? 'No clients match that search' : 'No clients yet'}
              hint={search ? 'Try a different name, phone or city.' : 'Add your first client to get started.'}
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingLeft: 24 }}>Client Profile</th>
                  <th style={{ textAlign: 'left' }}>Phone number</th>
                  <th style={{ textAlign: 'left' }}>Location</th>
                  <th style={{ textAlign: 'left' }}>Projects</th>
                  <th style={{ textAlign: 'right', paddingRight: 24 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => {
                  const clientProjects = (allProjects ?? []).filter((p) => p.clientId === c.id);
                  const isExpanded = expandedClientIds.has(c.id);

                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        className={`clickable ${isExpanded ? 'expanded-row' : ''}`}
                        onClick={() => toggleExpand(c.id)}
                        style={{
                          cursor: 'pointer',
                          background: isExpanded ? 'var(--surface-2)' : undefined,
                          borderBottom: isExpanded ? 'none' : undefined,
                        }}
                      >
                        <td style={{ textAlign: 'left', paddingLeft: 24 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12 }}>
                            <div
                              className="avatar-sq"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 'var(--radius-xs)',
                                background: '#eff6ff',
                                color: '#2563eb',
                                fontWeight: 700,
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {initials(c.name)}
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <div className="name" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {c.name}
                              </div>
                              {c.email && (
                                <div className="meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <Mail size={11} />
                                  <span>{c.email}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="num" style={{ textAlign: 'left', fontWeight: 500 }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                            <Phone size={12} style={{ color: 'var(--text-faint)' }} />
                            <span>{c.phone}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                            <MapPin size={12} style={{ color: 'var(--text-faint)' }} />
                            <span>{c.city || c.address || '—'}</span>
                          </div>
                        </td>
                        <td className="num" style={{ textAlign: 'left' }}>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '3px 10px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: 12,
                              fontWeight: 600,
                              background: isExpanded
                                ? '#dbeafe'
                                : clientProjects.length > 0
                                ? '#eff6ff'
                                : '#f8fafc',
                              color: isExpanded
                                ? '#1d4ed8'
                                : clientProjects.length > 0
                                ? '#2563eb'
                                : 'var(--text-faint)',
                              border: `1px solid ${
                                isExpanded
                                  ? '#93c5fd'
                                  : clientProjects.length > 0
                                  ? '#dbeafe'
                                  : 'var(--border)'
                              }`,
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            title={isExpanded ? 'Click to collapse project list' : 'Click to expand project list'}
                          >
                            <span>
                              {clientProjects.length} {clientProjects.length === 1 ? 'Project' : 'Projects'}
                            </span>
                            {isExpanded ? (
                              <ChevronUp size={13} strokeWidth={2.4} />
                            ) : (
                              <ChevronDown size={13} strokeWidth={2.4} />
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', paddingRight: 24 }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button
                              className="icon-btn"
                              onClick={() => setEditing(c)}
                              title={`Edit ${c.name}`}
                              aria-label={`Edit ${c.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              className="icon-btn btn-danger"
                              onClick={() => confirm.ask(c)}
                              title={`Delete ${c.name}`}
                              aria-label={`Delete ${c.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Dropdown Projects Detail Table Inside Another Table */}
                      {isExpanded && (
                        <tr className="nested-projects-row" style={{ background: 'var(--surface-2)' }}>
                          <td colSpan={5} style={{ padding: '0 0 16px 0', borderBottom: '2px solid var(--border)' }}>
                            <div
                              style={{
                                margin: '6px 20px 8px 24px',
                                background: '#ffffff',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                boxShadow: 'var(--shadow-xs)',
                                overflow: 'hidden',
                              }}
                            >
                              {/* Nested Header */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 18px',
                                  background: 'var(--surface-2)',
                                  borderBottom: '1px solid var(--border)',
                                  flexWrap: 'wrap',
                                  gap: 10,
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Building2 size={16} style={{ color: 'var(--accent)' }} />
                                  <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                                    Projects Detail — {c.name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: 10,
                                      background: clientProjects.length > 0 ? '#dbeafe' : 'var(--surface-3)',
                                      color: clientProjects.length > 0 ? '#1d4ed8' : 'var(--text-soft)',
                                    }}
                                  >
                                    {clientProjects.length} {clientProjects.length === 1 ? 'Site' : 'Sites'}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-primary btn-auto btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAddingProjectClientId(c.id);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    padding: '5px 12px',
                                    borderRadius: 'var(--radius-sm)',
                                  }}
                                >
                                  <Plus size={13} strokeWidth={2.4} />
                                  <span>Add Project for this Client</span>
                                </button>
                              </div>

                              {/* Nested Table or Empty */}
                              {clientProjects.length === 0 ? (
                                <div style={{ padding: '26px 20px', textAlign: 'center' }}>
                                  <p style={{ fontSize: 13, color: 'var(--text-soft)', margin: '0 0 12px' }}>
                                    No projects registered for this client yet.
                                  </p>
                                  <button
                                    type="button"
                                    className="btn btn-auto btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddingProjectClientId(c.id);
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                  >
                                    <Plus size={13} />
                                    <span>Create First Project</span>
                                  </button>
                                </div>
                              ) : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ margin: 0, width: '100%', fontSize: 12.5 }}>
                                    <thead>
                                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Project Name</th>
                                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Location</th>
                                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Status</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Contract Value</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Received</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Pending</th>
                                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Start Date</th>
                                        <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {clientProjects.map((p) => (
                                        <tr
                                          key={p.id}
                                          className="clickable"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/projects/${p.id}`);
                                          }}
                                          style={{
                                            cursor: 'pointer',
                                            transition: 'background 0.15s ease',
                                            borderBottom: '1px solid var(--border-subtle)',
                                          }}
                                        >
                                          <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--text)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                              <div
                                                style={{
                                                  width: 26,
                                                  height: 26,
                                                  borderRadius: 'var(--radius-xs)',
                                                  background: 'var(--surface-2)',
                                                  color: 'var(--text-soft)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <Building2 size={13} />
                                              </div>
                                              <span style={{ color: 'var(--text)', fontWeight: 600 }}>{p.name}</span>
                                            </div>
                                          </td>
                                          <td style={{ padding: '11px 14px', color: 'var(--text-soft)' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                              <MapPin size={11} style={{ color: 'var(--text-faint)' }} />
                                              <span>{p.location || '—'}</span>
                                            </div>
                                          </td>
                                          <td style={{ padding: '11px 14px' }}>
                                            <StatusChip value={p.status} />
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>
                                            {formatMoney(p.valuePaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 500, color: '#059669' }}>
                                            {formatMoney(p.paidPaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 500, color: p.pendingPaise > 0 ? '#d97706' : 'var(--text-faint)' }}>
                                            {formatMoney(p.pendingPaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', color: 'var(--text-soft)' }}>
                                            {formatDate(p.startDate)}
                                          </td>
                                          <td style={{ padding: '11px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                                              <button
                                                type="button"
                                                className="icon-btn"
                                                onClick={() => setEditingProject(p)}
                                                title={`Edit ${p.name}`}
                                                aria-label={`Edit ${p.name}`}
                                                style={{ width: 28, height: 28 }}
                                              >
                                                <Edit2 size={13} />
                                              </button>
                                              <button
                                                type="button"
                                                className="icon-btn btn-danger"
                                                onClick={() => confirmProject.ask(p)}
                                                title={`Delete ${p.name}`}
                                                aria-label={`Delete ${p.name}`}
                                                style={{ width: 28, height: 28 }}
                                              >
                                                <Trash2 size={13} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {adding && <ClientDrawer onClose={() => setAdding(false)} />}
      {editing && <ClientDrawer existing={editing} onClose={() => setEditing(null)} />}
      {addingProjectClientId && (
        <ProjectDrawer
          defaultClientId={addingProjectClientId}
          onClose={() => setAddingProjectClientId(null)}
        />
      )}
      {editingProject && (
        <ProjectDrawer
          existing={editingProject}
          onClose={() => setEditingProject(null)}
        />
      )}

      {confirm.target && (
        <Confirm
          title={`Delete Client: "${confirm.target.name}"?`}
          message="Are you sure you want to permanently delete this client? Please read the disclaimer below before confirming."
          confirmLabel="Delete Everything"
          danger
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        >
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              fontSize: 12.5,
              color: '#991b1b',
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠️ Disclaimer & Cascade Deletion</span>
            </div>
            <div>
              Deleting this client will permanently remove them from the database, along with:
            </div>
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li>All registered projects under this client</li>
              <li>All project transactions (client payments, purchase bills, subcontractor labour, site expenses)</li>
              <li>All architectural drawings, elevation files & attachments in storage</li>
            </ul>
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              This action cannot be undone.
            </div>
          </div>
        </Confirm>
      )}

      {confirmProject.target && (
        <Confirm
          title={`Delete Project: "${confirmProject.target.name}"?`}
          message="Are you sure you want to permanently delete this project? Please read the disclaimer below before confirming."
          confirmLabel="Delete Everything"
          danger
          busy={removeProject.isPending}
          onConfirm={() => void doDeleteProject()}
          onCancel={confirmProject.close}
        >
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              fontSize: 12.5,
              color: '#991b1b',
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠️ Disclaimer & Cascade Deletion</span>
            </div>
            <div>
              Deleting this project will permanently remove it from the database and storage, including:
            </div>
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li>All client payments, advances & receipts</li>
              <li>All material purchases, purchase bills & supplier records</li>
              <li>All subcontractor visits, labour batches & payments</li>
              <li>All site expenses, petty cash & vouchers</li>
              <li>All material consumption & stock transfer records</li>
              <li>All uploaded drawings, elevations & project files in storage</li>
            </ul>
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              This action cannot be undone.
            </div>
          </div>
        </Confirm>
      )}
    </Shell>
  );
}

/* --------------------------- Add / Edit Client Drawer --------------------------- */

const SURNAMES: Array<{ value: string; label: string }> = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Er', label: 'Er' },
];

const PROJECT_STATUSES: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'planning', label: 'Planning' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
];

function parseClientName(fullName: string) {
  const match = fullName.match(/^(Mr\.|Mr|Mrs\.|Mrs|Ms\.|Ms|Dr\.|Dr|Er\.|Er)\s+(.*)$/i);
  if (match && match[1] && match[2] !== undefined) {
    const raw = match[1].replace(/\./g, '');
    const title = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    const found = SURNAMES.find((s) => s.value.toLowerCase() === title.toLowerCase());
    return {
      surname: found ? found.value : 'Mr',
      name: match[2].trim(),
    };
  }
  return {
    surname: 'Mr',
    name: fullName.trim(),
  };
}

export interface ClientProjectDraft {
  tempId: string;
  name: string;
  valueRupees: string;
  location: string;
  start_date: string;
  status: ProjectStatus;
  description: string;
}

const EMPTY: NewClient = { name: '', phone: '', email: '', address: '', city: '' };

type EditableClient = Pick<ClientRow, 'id' | 'name' | 'phone' | 'email' | 'address' | 'city'>;

export function ClientDrawer({
  existing,
  onClose,
}: {
  existing?: EditableClient;
  onClose: () => void;
}) {
  const parsed = existing ? parseClientName(existing.name) : { surname: 'Mr', name: '' };
  const [surname, setSurname] = useState(parsed.surname);
  const [form, setForm] = useState<NewClient>(
    existing
      ? {
          name: parsed.name,
          phone: existing.phone,
          email: existing.email ?? '',
          address: existing.address ?? '',
          city: existing.city ?? '',
        }
      : EMPTY,
  );
  const [projects, setProjects] = useState<ClientProjectDraft[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useCreateClient();
  const update = useUpdateClient();
  const createProject = useCreateProject();
  const toast = useToast();
  const busy = create.isPending || update.isPending || createProject.isPending;

  const set = (k: keyof NewClient) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function handleAddProject() {
    setProjects((prev) => [
      ...prev,
      {
        tempId: crypto.randomUUID ? crypto.randomUUID() : `proj_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        name: '',
        valueRupees: '',
        location: form.city || '',
        start_date: '',
        status: 'ongoing',
        description: '',
      },
    ]);
  }

  function handleRemoveProject(tempId: string) {
    setProjects((prev) => prev.filter((p) => p.tempId !== tempId));
  }

  function updateProjectField(tempId: string, field: keyof ClientProjectDraft, val: string) {
    setProjects((prev) =>
      prev.map((p) => (p.tempId === tempId ? { ...p, [field]: val } : p)),
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const cleanName = form.name.replace(/^(Mr\.|Mr|Mrs\.|Mrs|Ms\.|Ms|Dr\.|Dr|Er\.|Er)\s+/i, '').trim();
    if (!cleanName) next.name = 'Client name is required';
    if (!form.phone.trim()) next.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length < 10) next.phone = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';

    // Validate any attached projects
    projects.forEach((p, idx) => {
      if (!p.name.trim()) {
        next[`proj_${p.tempId}_name`] = `Project #${idx + 1} name is required`;
      }
      const val = p.valueRupees.replace(/[₹,\s]/g, '');
      if (!val) {
        next[`proj_${p.tempId}_value`] = `Project #${idx + 1} contract budget is required`;
      } else if (!/^\d+(\.\d{1,2})?$/.test(val)) {
        next[`proj_${p.tempId}_value`] = `Enter a valid amount (e.g. 5000000)`;
      }
    });

    setErrors(next);
    if (Object.keys(next).length) return;

    const fullName = surname ? `${surname}. ${cleanName}` : cleanName;

    try {
      let clientId = existing?.id;
      if (existing) {
        await update.mutateAsync({ ...form, name: fullName, id: existing.id });
      } else {
        const res = await create.mutateAsync({ ...form, name: fullName });
        clientId = res.id;
      }

      if (clientId && projects.length > 0) {
        for (const p of projects) {
          await createProject.mutateAsync({
            client_id: clientId,
            name: p.name.trim(),
            valueRupees: p.valueRupees,
            location: p.location.trim() || undefined,
            start_date: p.start_date || undefined,
            status: p.status,
            description: p.description.trim() || undefined,
          });
        }
      }

      if (projects.length > 0) {
        toast.show(
          `${fullName} ${existing ? 'updated' : 'added'} with ${projects.length} project${projects.length > 1 ? 's' : ''}`,
        );
      } else {
        toast.show(`${fullName} ${existing ? 'updated' : 'added'}`);
      }
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the client', true);
    }
  }

  return (
    <Drawer
      title={existing ? 'Edit Client' : 'Add Client'}
      description={existing ? existing.name : 'Create a new client account profile'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Changes' : 'Create Client'}
      busy={busy}
      wide={projects.length > 0}
    >
      <div className="form-grid">
        <Field label="Surname">
          <Select
            value={surname}
            onChange={(val) => setSurname(val)}
            options={SURNAMES}
          />
        </Field>

        <Field label="Client Full Name" error={errors.name}>
          <input
            value={form.name}
            onChange={set('name')}
            autoFocus
            aria-invalid={!!errors.name}
          />
        </Field>

        <Field label="Primary Phone Number" error={errors.phone}>
          <input
            value={form.phone}
            onChange={set('phone')}
            inputMode="tel"
            aria-invalid={!!errors.phone}
          />
        </Field>

        <Field label="Email Address" error={errors.email} optional>
          <input
            type="email"
            value={form.email ?? ''}
            onChange={set('email')}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field label="Location (City)">
          <input
            value={form.city ?? ''}
            onChange={set('city')}
          />
        </Field>

        <Field label="Office / Postal Address" optional span2>
          <textarea
            value={form.address ?? ''}
            onChange={set('address')}
          />
        </Field>
      </div>

      {/* ---------------- Projects Section ---------------- */}
      <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
              Associated Projects {projects.length > 0 ? `(${projects.length})` : ''}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
              Add one or more projects directly under this client
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-auto"
            onClick={handleAddProject}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
          >
            <Plus size={14} />
            <span>{projects.length === 0 ? 'Add Project' : 'Add Another Project'}</span>
          </button>
        </div>

        {projects.length === 0 ? (
          <div
            style={{
              padding: '14px 16px',
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius)',
              background: 'var(--surface-muted, rgba(0,0,0,0.015))',
              fontSize: 12,
              color: 'var(--text-soft)',
            }}
          >
            No projects added yet. Click <strong>Add Project</strong> above to register projects for this client right now.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {projects.map((p, idx) => (
              <div
                key={p.tempId}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  padding: '16px 18px',
                  background: 'var(--surface)',
                  boxShadow: 'var(--shadow-xs)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#eff6ff',
                        color: '#2563eb',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                      {p.name.trim() ? p.name.trim() : `Project #${idx + 1}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-danger btn-auto btn-sm"
                    onClick={() => handleRemoveProject(p.tempId)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', fontSize: 11 }}
                    title="Remove this project"
                  >
                    <Trash2 size={12} />
                    <span>Remove</span>
                  </button>
                </div>

                <div className="form-grid">
                  <Field label="Project Name" error={errors[`proj_${p.tempId}_name`]} span2>
                    <input
                      value={p.name}
                      onChange={(e) => updateProjectField(p.tempId, 'name', e.target.value)}
                      aria-invalid={!!errors[`proj_${p.tempId}_name`]}
                    />
                  </Field>

                  <Field label="Site Location">
                    <input
                      value={p.location}
                      onChange={(e) => updateProjectField(p.tempId, 'location', e.target.value)}
                    />
                  </Field>

                  <Field label="Target Start Date" optional>
                    <input
                      type="date"
                      value={p.start_date}
                      onChange={(e) => updateProjectField(p.tempId, 'start_date', e.target.value)}
                    />
                  </Field>

                  <Field
                    label="Contract Budget (₹)"
                    error={errors[`proj_${p.tempId}_value`]}
                  >
                    <input
                      value={p.valueRupees}
                      onChange={(e) => updateProjectField(p.tempId, 'valueRupees', e.target.value)}
                      inputMode="decimal"
                      aria-invalid={!!errors[`proj_${p.tempId}_value`]}
                    />
                  </Field>

                  <Field label="Project Status">
                    <Select
                      value={p.status}
                      onChange={(val) => updateProjectField(p.tempId, 'status', val as ProjectStatus)}
                      options={PROJECT_STATUSES}
                    />
                  </Field>

                  <Field label="Project Scope & Notes" optional span2>
                    <textarea
                      value={p.description}
                      onChange={(e) => updateProjectField(p.tempId, 'description', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-sm btn-auto"
              onClick={handleAddProject}
              style={{
                alignSelf: 'flex-start',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
              }}
            >
              <Plus size={14} />
              <span>Add Another Project</span>
            </button>
          </div>
        )}
      </div>
    </Drawer>
  );
}
